import type { ChosenCards } from './cookbook/cardLooks'
import type { ChosenCookbookLook } from './cookbook/looks'
import { useQuery } from '@tanstack/react-query'
import type { Crop } from '../../shared/crop'
import type { Meal } from '../../shared/meals'
import { albumPhotoUrl } from './albums'
import { api } from './api'
import type { Space } from './families'
import type { Place } from './spaces'
import { photoUrl } from './photos'
import type { From } from './recipes'

// The picture beside a recipe: one of the recipe's own photos (its card) or a photo from an album.
// width and height are as it shows (cropped). A printed book crops the original the same way: original is the whole
// photo and crop the part kept (null for all of it).
export type Picture = {
  kind: 'recipe' | 'album'
  id: string
  ref: string
  width: number
  height: number
  original: { width: number; height: number }
  crop: Crop | null
}
export type PictureChoice = { kind: 'recipe' | 'album'; id: string }

export type CookbookSummary = {
  id: string
  title: string
  subtitle: string
  recipes: number
  cover: Picture | null
  // Whether you made it, for "Only mine".
  mine?: boolean
}
export type CookbookRecipe = {
  id: string
  title: string
  ingredients: string
  steps: string
  notes: string
  meals: Meal[]
  // Null for a recipe in no book.
  book: { id: string; title: string } | null
  from: From | null
  // What each photo is of, the card itself or the dish, is missing from an older server.
  photos: (Omit<Picture, 'kind'> & { of?: 'card' | 'food' })[]
}
export type CookbookPage = { recipe: CookbookRecipe; picture: Picture | null; chosen?: PictureChoice | null }
export type Cookbook = {
  id: string
  familyId: string
  title: string
  subtitle: string
  dedication: string
  // Divided into sections by meal (the pages come already in their sections).
  sections: boolean
  by: string
  space: Space | null
  // Who it's for: Everyone, or the places it's shared with that you're in (neither: only its maker). Missing from an
  // older server.
  everyone?: boolean
  sharedWith?: Place[]
  mine: boolean
  // Its maker's design for the printed book; missing from an older server.
  printDesign?: ChosenCookbookLook | null
  // And for its recipe cards.
  cardsDesign?: ChosenCards | null
  pages: CookbookPage[]
}
export type PickableRecipe = {
  id: string
  title: string
  // Null for a recipe in no book.
  book: { id: string; title: string } | null
  meals: Meal[]
  cover: string | null
}

export const useCookbooks = (familyId: string, spaceId?: string) =>
  useQuery({
    queryKey: ['cookbooks', familyId, spaceId ?? 'all'],
    queryFn: () =>
      api<{ cookbooks: CookbookSummary[] }>(
        `/families/${familyId}/cookbooks${spaceId ? `?space=${encodeURIComponent(spaceId)}` : ''}`,
      ),
  })

export const useCookbook = (id: string) =>
  useQuery({ queryKey: ['cookbook', id], queryFn: () => api<Cookbook>(`/cookbooks/${encodeURIComponent(id)}`) })

// Every recipe you can see in the circle, across its books, to pick from.
export const useFamilyRecipes = (familyId: string) =>
  useQuery({
    queryKey: ['family-recipes', familyId],
    queryFn: () => api<{ recipes: PickableRecipe[] }>(`/families/${familyId}/recipes`),
  })

// Where a picture is served, at the size asked for. Album photos have a medium copy; card photos fall back to display.
export function pictureUrl(picture: Pick<Picture, 'kind' | 'ref'>, size: 'thumb' | 'medium' | 'display' | 'original') {
  if (picture.kind === 'album') return albumPhotoUrl(picture.ref, size)
  return photoUrl(picture.ref, size === 'medium' ? 'display' : size)
}

export function pictureSrcSet(picture: Picture) {
  if (picture.kind === 'album')
    return `${pictureUrl(picture, 'thumb')} 480w, ${pictureUrl(picture, 'medium')} 1200w, ${pictureUrl(picture, 'display')} 2000w`
  return `${pictureUrl(picture, 'thumb')} 480w, ${pictureUrl(picture, 'display')} 2000w`
}
